import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postFormText } from '@/shared/api/api-client';

/**
 * Ingest a consent signed OUTSIDE our app (PROD-1243 / Case 3).
 *
 * `POST /api/Consent/IngestExternal` (multipart) — mirrors Scrips.Patient
 * `ConsentController.IngestExternal` → `IConsentService.IngestExternalConsentAsync`.
 * The clinic signs on its own e-sign tool (or prints → wet-signs → scans); we DON'T
 * own the document, so the server stores the uploaded PDF verbatim as the
 * `Type="Form"` patient document and creates an ACTIVE `ConsentRecord` with capability
 * `external` (no fill / stamp / PAdES). Returns the new ConsentRecord id.
 *
 * The form's `consentTemplateId` carries the classification (type + FHIR scope/category
 * the BE pins from the chosen template). Field names are `[FromForm]`-bound → multipart
 * keys must match the C# DTO property names exactly (camelCase, `file` for the PDF).
 *
 * The endpoint replies 201 with the id as a JSON string literal (`"…"`); parse it like
 * the template-upload hook (decode the JSON literal, fall back to trimming the quotes)
 * so a stray quote never poisons the returned id.
 */
export interface IngestExternalConsentInput {
  patientId: string;
  /** Links the ingested consent to an encounter (appointment upload) → the Type="Form"
   *  doc is encounter-scoped. Omit for a patient-scoped (chart) upload. */
  encounterId?: string | null;
  consentTemplateId: string;
  consentType: string;
  scopeCode: string;
  categoryCode: string;
  categoryDisplay: string;
  categorySystem: string;
  actorId?: string | null;
  actorName?: string | null;
  file: File;
}

export function useIngestExternalConsent(patientId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation<string, Error, IngestExternalConsentInput>({
    mutationFn: async (input) => {
      const form = new FormData();
      form.append('patientId', input.patientId);
      if (input.encounterId) form.append('encounterId', input.encounterId);
      form.append('consentTemplateId', input.consentTemplateId);
      form.append('consentType', input.consentType);
      form.append('scopeCode', input.scopeCode);
      form.append('categoryCode', input.categoryCode);
      form.append('categoryDisplay', input.categoryDisplay);
      form.append('categorySystem', input.categorySystem);
      if (input.actorId) form.append('actorId', input.actorId);
      if (input.actorName) form.append('actorName', input.actorName);
      form.append('file', input.file, input.file.name);

      const raw = await postFormText('/api/Consent/IngestExternal', form);
      let id: string;
      try {
        const parsed = JSON.parse(raw);
        id = typeof parsed === 'string' ? parsed : raw;
      } catch {
        id = raw.replace(/^\s*"+|"+\s*$/g, '').trim();
      }
      // The endpoint replies 201 with `Guid.Empty` (all-zeros) on a swallowed
      // failure (bad PDF, cross-org template, server error) — a 2xx body, so without
      // this guard a failed ingest would look like success (no doc, no error). Treat
      // the empty Guid as a failure so the global mutation toast fires.
      if (/^0{8}-0{4}-0{4}-0{4}-0{12}$/i.test(id.trim())) {
        throw new Error("Couldn't upload the signed consent. Check the file and try again.");
      }
      return id;
    },
    onSuccess: () => {
      // The ingested consent lands as a new Type="Form" PatientDocument — refresh the
      // chart's Docs list + the patient's consent list so it shows without a manual reload.
      if (patientId) {
        void queryClient.invalidateQueries({ queryKey: ['patient-documents', patientId] });
        void queryClient.invalidateQueries({ queryKey: ['patient-consents', patientId] });
      }
    },
  });
}
